import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdminService } from '../../services/adminService';

const FAMILY_COLOR = {
  homophone: 'info',
  agreement: 'success',
  conjugation: 'secondary',
  lexical: 'warning',
  hors_taxonomie: 'default',
};

export default function ErrorClassificationHeuristicPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState('');
  const [classifyingOnly, setClassifyingOnly] = useState(true);
  const [expected, setExpected] = useState('maisons');
  const [produced, setProduced] = useState('maison');
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [probeError, setProbeError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await AdminService.getErrorClassificationHeuristic();
        if (!cancelled) setCatalog(data);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Impossible de lire l\'heuristique');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tryPair = async () => {
    setProbing(true);
    setProbeError(null);
    try {
      const data = await AdminService.classifyErrorHeuristic({ expected, produced });
      setProbe(data);
    } catch (e) {
      setProbeError(e?.message || 'Échec du test');
      setProbe(null);
    } finally {
      setProbing(false);
    }
  };

  const q = search.trim().toLowerCase();
  const rules = useMemo(() => {
    const all = catalog?.rules || [];
    return all.filter((r) => {
      if (classifyingOnly && !r.classifies) return false;
      if (!q) return true;
      const hay = [
        r.trapId,
        r.observedNotionId,
        r.observedNotionLabel,
        r.errorFamily,
        ...(r.keys || []),
        r.caveat,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [catalog, classifyingOnly, q]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!catalog) return null;

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 920 }}>
        Première règle qui matche gagne (ordre ci-dessous). Les règles sans clé de
        compétence (filet graphémique) ne classent rien. Un mot avec la ponctuation
        collée (<code>belles.</code>) ne matche pas <code>belles</code>.{' '}
        <code>±s</code> / <code>±x</code> : si l&apos;une des deux formes est
        exclusivement un nom (Lexique N) → pluriel de nom, sinon accord d&apos;adjectif.
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Tester un couple</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <TextField
              size="small"
              label="Attendu"
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
            />
            <TextField
              size="small"
              label="Produit"
              value={produced}
              onChange={(e) => setProduced(e.target.value)}
            />
            <Button variant="contained" onClick={tryPair} disabled={probing}>
              {probing ? '…' : 'Classer'}
            </Button>
          </Stack>
          {probeError && <Alert severity="error" sx={{ mt: 2 }}>{probeError}</Alert>}
          {probe && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {probe.expectedNorm} → {probe.producedNorm}
              </Typography>
              {probe.match ? (
                <Alert
                  severity={probe.match.caveat ? 'warning' : 'success'}
                  sx={{ mt: 1 }}
                >
                  <strong>{probe.match.trapId}</strong>
                  {' → '}
                  {probe.match.observedNotionLabel || probe.match.observedNotionId || '—'}
                  {probe.match.observedNotionId ? ` (${probe.match.observedNotionId})` : ''}
                  {` · ${probe.match.errorFamily}`}
                  {probe.match.caveat ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>{probe.match.caveat}</Typography>
                  ) : null}
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Aucune règle ne matche — le couple irait au cache, puis au LLM.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Clusters d&apos;homophones</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Si aucune règle unidirectionnelle ne matche, un couple dont les deux
            formes sont dans le même cluster est classé <code>homophone_cluster</code>.
          </Typography>
          <Stack spacing={1}>
            {(catalog.clusters || []).map((c) => (
              <Stack
                key={c.members.join('-')}
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
              >
                {c.members.map((m) => (
                  <Chip key={m} size="small" label={m} />
                ))}
                <Typography variant="body2">
                  → {c.observedNotionLabel || c.observedNotionId} ({c.observedNotionId})
                </Typography>
                {c.caveat && (
                  <Tooltip title={c.caveat}>
                    <Chip size="small" color="warning" variant="outlined" label="trou taxonomie" />
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography variant="h6">
          Règles ({catalog.classifyingRuleCount} classifiantes / {catalog.ruleCount})
        </Typography>
        <TextField
          size="small"
          label="Filtrer"
          placeholder="remove_s, accords…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={classifyingOnly}
              onChange={(e) => setClassifyingOnly(e.target.checked)}
            />
          }
          label="Uniquement celles qui classent"
        />
      </Stack>

      <TableContainer sx={{ maxHeight: 640 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="right">#</TableCell>
              <TableCell>Trap</TableCell>
              <TableCell>Clés</TableCell>
              <TableCell>Notion</TableCell>
              <TableCell>Famille</TableCell>
              <TableCell>Limite</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((r) => (
              <TableRow
                key={`${r.order}-${r.trapId}`}
                hover
                sx={r.caveat ? { bgcolor: 'action.selected' } : undefined}
              >
                <TableCell align="right">{r.order}</TableCell>
                <TableCell>
                  <code>{r.trapId}</code>
                  {!r.classifies && (
                    <Chip size="small" label="filet" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  {(r.keys || []).map((k) => (
                    <Chip key={k} size="small" label={k} sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                  {(r.keys || []).length === 0 ? '—' : null}
                </TableCell>
                <TableCell>
                  {r.observedNotionId ? (
                    <Box>
                      <Typography variant="body2">
                        {r.observedNotionLabel || r.observedNotionId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.observedNotionId}
                      </Typography>
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  {r.errorFamily ? (
                    <Chip
                      size="small"
                      label={r.errorFamily}
                      color={FAMILY_COLOR[r.errorFamily] || 'default'}
                      variant="outlined"
                    />
                  ) : '—'}
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  {r.caveat ? (
                    <Typography variant="caption" color="warning.dark">{r.caveat}</Typography>
                  ) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
